import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Copy, Share2, Eye, Settings, CreditCard, ExternalLink, RefreshCw } from 'lucide-react';
import Portal from '../UI/Portal';
import { useNameCard } from '../../hooks/useNameCard';
import { nameCardAPI } from '../../services/api';
import toast from '../../utils/toast';

/**
 * Full-screen QR code overlay for the FAB quick action.
 * Three states: active card, no card, paused card.
 */
const NameCardQuickShare = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const {
    nameCard, hasCard, isLoading,
    updateNameCard, isUpdating,
  } = useNameCard();
  const [qrUrl, setQrUrl] = useState(null);

  useEffect(() => {
    if (isOpen && nameCard?.is_active) {
      nameCardAPI.getQRCode()
        .then(res => setQrUrl(URL.createObjectURL(res.data)))
        .catch(() => {});
    }
    return () => {
      if (qrUrl) URL.revokeObjectURL(qrUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, nameCard?.is_active, nameCard?.id]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleCopyLink = useCallback(async () => {
    if (!nameCard?.public_url) return;
    try {
      await navigator.clipboard.writeText(nameCard.public_url);
      toast.success('Link copied');
    } catch {
      toast.error('Failed to copy');
    }
  }, [nameCard?.public_url]);

  const handleShare = useCallback(async () => {
    if (!nameCard?.public_url || !navigator.share) return;
    try {
      await navigator.share({
        title: `${nameCard.staff_name} - Contact Card`,
        text: `Contact card for ${nameCard.staff_name}`,
        url: nameCard.public_url,
      });
    } catch (err) {
      if (err.name !== 'AbortError') toast.error('Sharing failed');
    }
  }, [nameCard]);

  const handleSetup = useCallback(() => {
    onClose();
    navigate('/profile?tab=legal');
  }, [onClose, navigate]);

  const handleSettings = useCallback(() => {
    onClose();
    navigate('/profile?tab=name-card');
  }, [onClose, navigate]);

  const handleReactivate = useCallback(() => {
    updateNameCard({ is_active: true });
  }, [updateNameCard]);

  if (!isOpen) return null;

  return (
    <Portal>
      <div
        className="fixed inset-0 z-[9999] bg-black/95 flex flex-col items-center justify-center"
        onClick={onClose}
      >
        <div
          className="relative w-full max-w-sm mx-auto px-6"
          onClick={e => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute -top-12 right-0 p-2 text-white/60 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-lg"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" />
            </div>
          ) : hasCard && nameCard?.is_active ? (
            /* Active card state */
            <>
              {/* QR Code */}
              {qrUrl && (
                <div className="bg-white p-4 rounded-2xl mx-auto w-fit mb-6">
                  <img src={qrUrl} alt="Name Card QR" className="w-56 h-56 sm:w-64 sm:h-64" />
                </div>
              )}

              {/* Name + title */}
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-white">{nameCard.staff_name}</h2>
                {nameCard.staff_designation && (
                  <p className="text-base text-gray-300">{nameCard.staff_designation}</p>
                )}
                {nameCard.staff_department && (
                  <p className="text-sm text-gray-400">Winner Engineering</p>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex gap-3 justify-center mb-6">
                <button
                  onClick={handleCopyLink}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white border border-white/30 rounded-full hover:bg-white/10 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  <Copy className="w-4 h-4" />
                  Copy Link
                </button>
                {typeof navigator.share === 'function' && (
                  <button
                    onClick={handleShare}
                    className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white border border-white/30 rounded-full hover:bg-white/10 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                )}
              </div>

              {/* Footer info */}
              <div className="text-center space-y-2">
                <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                  <Eye className="w-3.5 h-3.5" />
                  {nameCard.view_count || 0} views
                </p>
                <button
                  onClick={handleSettings}
                  className="text-xs text-gray-500 hover:text-gray-300 flex items-center justify-center gap-1 mx-auto transition-colors"
                >
                  <Settings className="w-3.5 h-3.5" />
                  Card Settings
                </button>
              </div>
            </>
          ) : hasCard && !nameCard?.is_active ? (
            /* Paused card state */
            <>
              {qrUrl && (
                <div className="bg-white/20 p-4 rounded-2xl mx-auto w-fit mb-6 opacity-40">
                  <img src={qrUrl} alt="Name Card QR (paused)" className="w-48 h-48 grayscale" />
                </div>
              )}

              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-white">{nameCard.staff_name}</h2>
                {nameCard.staff_designation && (
                  <p className="text-base text-gray-300">{nameCard.staff_designation}</p>
                )}
              </div>

              <div className="bg-amber-500/20 border border-amber-500/40 rounded-xl p-4 text-center mb-6">
                <p className="text-sm text-amber-300 font-medium">Your card is currently paused</p>
                <p className="text-xs text-amber-400/70 mt-1">Visitors see "card unavailable"</p>
              </div>

              <button
                onClick={handleReactivate}
                disabled={isUpdating}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-white bg-blue-700 rounded-full hover:bg-blue-800 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black mb-4"
              >
                <RefreshCw className={`w-4 h-4 ${isUpdating ? 'animate-spin' : ''}`} />
                {isUpdating ? 'Reactivating...' : 'Reactivate Card'}
              </button>

              <button
                onClick={handleSettings}
                className="w-full text-center text-xs text-gray-500 hover:text-gray-300 flex items-center justify-center gap-1 transition-colors"
              >
                <Settings className="w-3.5 h-3.5" />
                Card Settings
              </button>
            </>
          ) : (
            /* No card state */
            <>
              <div className="text-center mb-6">
                <CreditCard className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-white mb-2">Digital Name Card</h2>
                <p className="text-sm text-gray-400">
                  Share your contact info instantly with a scannable QR code.
                </p>
              </div>

              <button
                onClick={handleSetup}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-white bg-blue-700 rounded-full hover:bg-blue-800 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black mb-4"
              >
                Set Up Now
                <ExternalLink className="w-4 h-4" />
              </button>

              <p className="text-center text-xs text-gray-500">Takes less than a minute</p>
            </>
          )}
        </div>
      </div>
    </Portal>
  );
};

export default NameCardQuickShare;
